import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMsal } from '@azure/msal-react';
import {
    Box,
    Button,
    CircularProgress,
    Typography,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Alert
} from '@mui/material';
import {
    CloudUpload,
    InsertDriveFile,
    Delete,
    CheckCircle,
    Error as ErrorIcon
} from '@mui/icons-material';
import axios from 'axios';
import { ConversionType, ConversionOption } from '../types';

interface FileConverterProps {
    conversionType: ConversionType;
    option: ConversionOption;
}

const FileConverter: React.FC<FileConverterProps> = ({ conversionType, option }) => {
    const { instance } = useMsal();
    const [files, setFiles] = useState<File[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [downloadDialog, setDownloadDialog] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState<string>('');
    const [filename, setFilename] = useState<string>('');

    // Opções de configuração para MP4 to GIF
    const [maxWidth, setMaxWidth] = useState<string>('');
    const [fps, setFps] = useState<string>('10');
    const [startTime, setStartTime] = useState<string>('');
    const [duration, setDuration] = useState<string>('');
    const [colors, setColors] = useState<string>('256');

    const getAcceptedTypes = () => {
        if (!option || !option.acceptedTypes) {
            return {};
        }

        const typeMap: { [key: string]: string[] } = {
            '.doc': ['application/msword'],
            '.docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
            '.pdf': ['application/pdf'],
            '.jpg': ['image/jpeg'],
            '.jpeg': ['image/jpeg'],
            '.png': ['image/png'],
            '.csv': ['text/csv'],
            '.xlsx': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
            '.xls': ['application/vnd.ms-excel'],
            '.json': ['application/json'],
            '.txt': ['text/plain'],
            '.mp4': ['video/mp4'],
            '.mov': ['video/quicktime'],
            '.avi': ['video/x-msvideo'],
            '.mkv': ['video/x-matroska'],
        };

        const acceptedMimeTypes: { [key: string]: string[] } = {};
        option.acceptedTypes.forEach(ext => {
            if (typeMap[ext]) {
                acceptedMimeTypes[typeMap[ext][0]] = [ext];
            }
        });

        return acceptedMimeTypes;
    };

    const onDrop = useCallback((acceptedFiles: File[]) => {
        const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

        // Verificar tamanho dos arquivos
        const oversizedFiles = acceptedFiles.filter(file => file.size > MAX_FILE_SIZE);
        if (oversizedFiles.length > 0) {
            setError(`Alguns arquivos são muito grandes. Tamanho máximo permitido: 100MB. Arquivos problemáticos: ${oversizedFiles.map(f => f.name).join(', ')}`);
            return;
        }

        // Verificar tamanho total se múltiplos arquivos
        if (option?.multiple) {
            const totalSize = [...files, ...acceptedFiles].reduce((sum, file) => sum + file.size, 0);
            if (totalSize > MAX_FILE_SIZE) {
                setError('O tamanho total dos arquivos excede 100MB. Por favor, remova alguns arquivos ou use arquivos menores.');
                return;
            }
            setFiles(prev => [...prev, ...acceptedFiles]);
        } else {
            setFiles([acceptedFiles[0]]);
        }
        setError(null);
    }, [option?.multiple, files]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: getAcceptedTypes(),
        multiple: option?.multiple || false,
    });

    const removeFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const clearAllFiles = () => {
        setFiles([]);
    };

    const getEndpointUrl = () => {
        const baseUrl = '/api';
        switch (conversionType) {
            case 'docx-to-pdf':
                return `${baseUrl}/convert/docx-to-pdf`;
            case 'pdf-to-docx':
                return `${baseUrl}/convert/pdf-to-docx`;
            case 'merge-pdfs':
                return `${baseUrl}/merge-pdfs`;
            case 'pdf-to-jpg':
                return `${baseUrl}/convert/pdf-to-jpg`;
            case 'jpg-to-pdf':
                return `${baseUrl}/convert/jpg-to-pdf`;
            case 'csv-to-pdf':
                return `${baseUrl}/convert/csv-to-pdf`;
            case 'csv-to-xlsx':
                return `${baseUrl}/convert/csv-to-xlsx`;
            case 'excel-to-pdf':
                return `${baseUrl}/convert/excel-to-pdf`;
            case 'pdf-to-excel':
                return `${baseUrl}/convert/pdf-to-excel`;
            case 'pdf-to-base64':
                return `${baseUrl}/convert/pdf-to-base64`;
            case 'base64-to-pdf':
                return `${baseUrl}/convert/base64-to-pdf`;
            case 'split-pdf':
                return `${baseUrl}/split-pdf`;
            case 'compress-pdf':
                return `${baseUrl}/compress-pdf`;
            case 'mp4-to-gif':
                return `${baseUrl}/convert/mp4-to-gif`;
            default:
                return `${baseUrl}/health`;
        }
    };

    const getDefaultFilename = () => {
        if (files.length === 0) return 'converted';

        const firstFile = files[0];
        if (!firstFile || !firstFile.name) return 'converted';

        const baseName = firstFile.name.split('.')[0];

        switch (conversionType) {
            case 'docx-to-pdf':
            case 'csv-to-pdf':
            case 'excel-to-pdf':
            case 'jpg-to-pdf':
                return `${baseName}.pdf`;
            case 'pdf-to-docx':
                return `${baseName}.docx`;
            case 'pdf-to-excel':
            case 'csv-to-xlsx':
                return `${baseName}.xlsx`;
            case 'pdf-to-base64':
                return `${baseName}_base64.json`;
            case 'base64-to-pdf':
                return `${baseName}.pdf`;
            case 'merge-pdfs':
                return 'merged.pdf';
            case 'pdf-to-jpg':
                return `${baseName}_images.zip`;
            case 'split-pdf':
                return `${baseName}_pages.zip`;
            case 'compress-pdf':
                return `${baseName}_compressed.pdf`;
            case 'mp4-to-gif':
                return `${baseName}.gif`;
            default:
                return 'converted';
        }
    };

    const getExpectedExtension = () => {
        switch (conversionType) {
            case 'docx-to-pdf':
            case 'csv-to-pdf':
            case 'excel-to-pdf':
            case 'jpg-to-pdf':
            case 'base64-to-pdf':
            case 'merge-pdfs':
            case 'compress-pdf':
                return '.pdf';
            case 'pdf-to-docx':
                return '.docx';
            case 'pdf-to-excel':
            case 'csv-to-xlsx':
                return '.xlsx';
            case 'pdf-to-base64':
                return '.json';
            case 'pdf-to-jpg':
            case 'split-pdf':
                return '.zip';
            case 'mp4-to-gif':
                return '.gif';
            default:
                return '';
        }
    };

    const ensureCorrectExtension = (filename: string) => {
        const expectedExtension = getExpectedExtension();
        if (!expectedExtension) return filename;

        // Remove qualquer extensão existente e adiciona a correta
        const nameWithoutExtension = filename.replace(/\.[^/.]+$/, '');
        return nameWithoutExtension + expectedExtension;
    };

    const getFilenameWithoutExtension = (filename: string) => {
        return filename.replace(/\.[^/.]+$/, '');
    };

    const handleConversion = async () => {
        if (files.length === 0) {
            setError('Por favor, selecione pelo menos um arquivo.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Obter token de acesso
            let account = instance.getActiveAccount();

            // Se não há conta ativa, tentar obter a primeira conta disponível
            if (!account) {
                const accounts = instance.getAllAccounts();

                if (accounts.length > 0) {
                    account = accounts[0];
                    // Definir como conta ativa
                    instance.setActiveAccount(account);
                } else {
                    throw new Error('Nenhuma conta encontrada. Por favor, faça login novamente.');
                }
            }

            let tokenResponse;
            try {
                tokenResponse = await instance.acquireTokenSilent({
                    scopes: ['User.Read'],
                    account: account
                });
            } catch (tokenError: any) {
                // Se falhar ao obter token silenciosamente, tentar interativo
                if (tokenError.name === 'InteractionRequiredAuthError') {
                    tokenResponse = await instance.acquireTokenPopup({
                        scopes: ['User.Read'],
                        account: account
                    });
                } else {
                    throw new Error(`Erro ao obter token: ${tokenError.message}`);
                }
            }

            const formData = new FormData();

            if (option?.multiple) {
                files.forEach((file) => {
                    formData.append('files', file);
                });
            } else {
                formData.append('file', files[0]);
            }

            // Adicionar parâmetros de configuração para MP4 to GIF
            if (conversionType === 'mp4-to-gif') {
                if (maxWidth && maxWidth.trim() !== '') {
                    formData.append('max_width', maxWidth);
                }
                if (fps && fps.trim() !== '') {
                    formData.append('fps', fps);
                }
                if (startTime && startTime.trim() !== '') {
                    formData.append('start_time', startTime);
                }
                if (duration && duration.trim() !== '') {
                    formData.append('duration', duration);
                }
                if (colors && colors.trim() !== '') {
                    formData.append('colors', colors);
                }
            }

            const response = await axios.post(getEndpointUrl(), formData, {
                responseType: 'blob',
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${tokenResponse.accessToken}`,
                },
                timeout: 600000, // 10 minutos para conversão de vídeo
            });

            // Preparar para download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const defaultFilename = getDefaultFilename();

            setDownloadUrl(url);
            setFilename(defaultFilename);
            setDownloadDialog(true);

        } catch (err: any) {
            console.error('Erro na conversão:', err);

            if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
                setError('Tempo limite excedido. A conversão está demorando mais que o esperado. Tente com um vídeo menor ou reduza a duração.');
            } else if (err.message === 'Usuário não autenticado' || err.message === 'Nenhuma conta encontrada. Por favor, faça login novamente.') {
                setError('Erro de autenticação. Por favor, faça login novamente.');
            } else if (err.message?.includes('Erro ao obter token')) {
                setError(`Erro de autenticação: ${err.message}`);
            } else if (err.response?.status === 401) {
                setError('Token inválido ou expirado. Por favor, faça login novamente.');
            } else if (err.response?.status === 403) {
                setError('Acesso negado. Verifique suas permissões.');
            } else if (err.response?.status === 413) {
                setError('Arquivo muito grande. O tamanho máximo permitido é 100MB. Tente comprimir o arquivo ou dividir em partes menores.');
            } else if (err.response?.status === 500) {
                // Tentar ler a mensagem de erro do backend
                if (err.response?.data) {
                    try {
                        // Se for um blob, converter para texto
                        if (err.response.data instanceof Blob) {
                            const errorText = await err.response.data.text();
                            try {
                                const errorJson = JSON.parse(errorText);
                                setError(errorJson.detail || 'Erro no servidor durante a conversão.');
                            } catch {
                                setError(errorText || 'Erro no servidor durante a conversão. Verifique se o FFmpeg está instalado.');
                            }
                        } else {
                            setError(err.response.data.detail || 'Erro no servidor durante a conversão. Verifique se o FFmpeg está instalado.');
                        }
                    } catch (parseError) {
                        console.error('Erro ao processar resposta de erro:', parseError);
                        setError('Erro no servidor durante a conversão. Verifique se o FFmpeg está instalado e se o vídeo é válido.');
                    }
                } else {
                    setError('Erro no servidor durante a conversão. Verifique se o FFmpeg está instalado.');
                }
            } else if (err.message) {
                setError(err.message);
            } else {
                setError(err.response?.data?.detail || 'Erro ao converter arquivo. Por favor, tente novamente.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = () => {
        const finalFilename = ensureCorrectExtension(filename);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', finalFilename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
        setDownloadDialog(false);
        setDownloadUrl('');
        setFilename('');
    };

    const handleCloseDialog = () => {
        window.URL.revokeObjectURL(downloadUrl);
        setDownloadDialog(false);
        setDownloadUrl('');
        setFilename('');
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            {/* Upload Area */}
            <Paper
                {...getRootProps()}
                elevation={0}
                sx={{
                    border: `2px dashed ${isDragActive ? (option?.color || '#e0e0e0') : '#e0e0e0'}`,
                    borderRadius: 3,
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor: isDragActive ? `${option?.color || '#e0e0e0'}10` : 'grey.50',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        bgcolor: `${option?.color || '#e0e0e0'}08`,
                        borderColor: option?.color || '#e0e0e0',
                    },
                }}
            >
                <input {...getInputProps()} />
                <CloudUpload
                    sx={{
                        fontSize: 64,
                        color: isDragActive ? (option?.color || '#e0e0e0') : 'grey.400',
                        mb: 2
                    }}
                />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {isDragActive
                        ? 'Solte os arquivos aqui...'
                        : `Arraste e solte ${option?.multiple ? 'os arquivos' : 'o arquivo'} aqui`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    ou clique para selecionar
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                    Tamanho máximo: 100MB por arquivo
                </Typography>
                {option?.acceptedTypes && option.acceptedTypes.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
                        {option.acceptedTypes.map((type) => (
                            <Chip
                                key={type}
                                label={type}
                                size="small"
                                sx={{
                                    bgcolor: `${option?.color || '#e0e0e0'}20`,
                                    color: option?.color || '#e0e0e0',
                                    fontWeight: 600
                                }}
                            />
                        ))}
                    </Box>
                )}
            </Paper>

            {/* Configurações para MP4 to GIF */}
            {conversionType === 'mp4-to-gif' && files.length > 0 && (
                <Paper elevation={0} sx={{ mt: 3, borderRadius: 2, bgcolor: 'grey.50', p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Opções de Conversão
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            <TextField
                                fullWidth
                                label="Largura Máxima (px)"
                                type="number"
                                value={maxWidth}
                                onChange={(e) => setMaxWidth(e.target.value)}
                                helperText="Resolução do GIF (preserva proporção)"
                                variant="outlined"
                                size="small"
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                fullWidth
                                label="FPS"
                                type="number"
                                value={fps}
                                onChange={(e) => setFps(e.target.value)}
                                helperText="Taxa de frames (10-15 recomendado)"
                                variant="outlined"
                                size="small"
                                inputProps={{ min: 1, max: 30 }}
                                sx={{ flex: 1 }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            <TextField
                                fullWidth
                                label="Tempo Inicial (segundos)"
                                type="number"
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                                helperText="Segundo onde começar a conversão"
                                variant="outlined"
                                size="small"
                                inputProps={{ min: 0, step: 0.1 }}
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                fullWidth
                                label="Duração (segundos)"
                                type="number"
                                value={duration}
                                onChange={(e) => setDuration(e.target.value)}
                                helperText="Quantos segundos converter (deixe vazio para todo o vídeo)"
                                variant="outlined"
                                size="small"
                                inputProps={{ min: 0, step: 0.1 }}
                                sx={{ flex: 1 }}
                            />
                        </Box>
                        <TextField
                            fullWidth
                            label="Cores na Paleta"
                            type="number"
                            value={colors}
                            onChange={(e) => setColors(e.target.value)}
                            helperText="Número de cores (máximo 256, mais cores = melhor qualidade)"
                            variant="outlined"
                            size="small"
                            inputProps={{ min: 2, max: 256 }}
                        />
                    </Box>
                </Paper>
            )}

            {/* Files List */}
            {files.length > 0 && (
                <Paper elevation={0} sx={{ mt: 3, borderRadius: 2, bgcolor: 'grey.50' }}>
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Arquivos Selecionados ({files.length})
                        </Typography>
                        {files.length > 1 && (
                            <Button
                                size="small"
                                onClick={clearAllFiles}
                                sx={{ color: 'error.main' }}
                            >
                                Limpar Todos
                            </Button>
                        )}
                    </Box>
                    <List sx={{ pt: 0 }}>
                        {files.map((file, index) => (
                            <ListItem
                                key={index}
                                sx={{
                                    bgcolor: 'white',
                                    mb: 1,
                                    mx: 2,
                                    borderRadius: 2,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                }}
                            >
                                <ListItemIcon>
                                    <InsertDriveFile sx={{ color: option?.color || '#e0e0e0' }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={file.name}
                                    secondary={formatFileSize(file.size)}
                                />
                                <IconButton
                                    edge="end"
                                    onClick={() => removeFile(index)}
                                    sx={{ color: 'error.main' }}
                                >
                                    <Delete />
                                </IconButton>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}

            {/* Error Alert */}
            {error && (
                <Alert
                    severity="error"
                    sx={{ mt: 3, borderRadius: 2 }}
                    icon={<ErrorIcon />}
                >
                    {error}
                </Alert>
            )}

            {/* Convert Button */}
            <Button
                variant="contained"
                onClick={handleConversion}
                disabled={loading || files.length === 0}
                fullWidth
                size="large"
                sx={{
                    mt: 3,
                    py: 2,
                    bgcolor: option?.color || '#e0e0e0',
                    borderRadius: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                        bgcolor: option?.color || '#e0e0e0',
                        filter: 'brightness(0.9)',
                    },
                    '&:disabled': {
                        bgcolor: 'grey.300',
                    },
                }}
            >
                {loading ? (
                    <>
                        <CircularProgress size={24} color="inherit" sx={{ mr: 2 }} />
                        Convertendo...
                    </>
                ) : (
                    `${option?.button || 'Converter'}`
                )}
            </Button>

            {/* Download Dialog */}
            <Dialog
                open={downloadDialog}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                    <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Conversão Concluída!
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
                        Seu arquivo foi convertido com sucesso. Você pode alterar o nome antes de baixar.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Nome do arquivo (sem extensão)"
                        fullWidth
                        variant="outlined"
                        value={getFilenameWithoutExtension(filename)}
                        onChange={(e) => setFilename(e.target.value + getExpectedExtension())}
                        helperText={`O arquivo será salvo como: ${getFilenameWithoutExtension(filename) || 'nome'}${getExpectedExtension()}`}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button
                        onClick={handleCloseDialog}
                        sx={{ borderRadius: 2, px: 3 }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleDownload}
                        variant="contained"
                        sx={{
                            borderRadius: 2,
                            px: 4,
                            bgcolor: option?.color || '#e0e0e0',
                            '&:hover': {
                                bgcolor: option?.color || '#e0e0e0',
                                filter: 'brightness(0.9)',
                            },
                        }}
                    >
                        Baixar Arquivo
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FileConverter; 